/**
 * AppointmentDetailDialog
 *
 * Modal showing full appointment info with state-appropriate action buttons.
 * Replaces the lightweight bottom drawer from Session 2/3.
 *
 * Status-driven button visibility:
 *  - REQUESTED   → Edit, Mark Arrived, Reschedule, Cancel
 *  - APPROVED    → Edit, Mark Arrived, Reschedule, Cancel
 *  - ARRIVED     → Edit, Mark Departed, Cancel
 *  - DEPARTED    → Edit (read-only otherwise)
 *  - NOSHOW      → Edit (read-only otherwise)
 *  - CANCELLED   → Edit (read-only otherwise)
 *
 * Edit covers content (carrier, client, PO/consignees, references, comments,
 * capabilities, and direction). Direction-change is server-restricted to
 * REQUESTED/APPROVED. Time/door changes still go through Reschedule.
 */
import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
    CheckCircle2,
    LogIn,
    LogOut,
    AlertCircle,
    Pencil,
    XCircle,
    Ban,
    UserX,
    Loader2,
    FileText,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
    ApiCallError,
    markArrived as apiMarkArrived,
    markDeparted as apiMarkDeparted,
    cancelAppointment as apiCancel,
    approveAppointment as apiApprove,
    rejectAppointment as apiReject,
    markNoShow as apiMarkNoShow,
    type NotifyEvent,
} from '@/api/client';
import type {
    AppointmentDto,
    AppointmentStatusCode,
    CalendarDoorDto,
} from '@/api/types';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConformingPanel } from '@/components/ConformingPanel';
import { JourneyPanel } from '@/components/JourneyPanel';
import {
    getAppointmentPaperwork, markPaperworkReviewed, type PaperworkStatusDto,
} from '@/api/client';
import { OutboundReleaseDialog } from '@/components/OutboundReleaseDialog';
import type { ReleaseOverrideDto } from '@/api/types';

const STATUS_LABEL: Record<AppointmentStatusCode, string> = {
    REQUESTED: 'Requested',
    APPROVED:  'Approved',
    ARRIVED:   'Arrived',
    DEPARTED:  'Departed',
    NOSHOW:    'No show',
    CANCELLED: 'Cancelled',
};

const STATUS_BADGE_CLASSES: Record<AppointmentStatusCode, string> = {
    REQUESTED: 'bg-status-requested/15 text-status-requested border-status-requested/40',
    APPROVED:  'bg-status-approved/15 text-status-approved border-status-approved/40',
    ARRIVED:   'bg-status-arrived/15 text-status-arrived border-status-arrived/40',
    DEPARTED:  'bg-status-departed/15 text-status-departed border-status-departed/40',
    NOSHOW:    'bg-status-noshow/15 text-status-noshow border-status-noshow/40',
    CANCELLED: 'bg-status-cancelled/15 text-status-cancelled border-status-cancelled/40',
};

interface AppointmentDetailDialogProps {
    open: boolean;
    onClose: () => void;
    appt: AppointmentDto;
    doors: CalendarDoorDto[];
    getToken: () => Promise<string>;
    onChanged: () => void;
    onReschedule: (appt: AppointmentDto) => void;
    onEdit: (appt: AppointmentDto) => void;
    /**
     * ⭐ OPEN THE LOAD'S PAPERWORK — Pete, 2026-08-25: *"when you click on an
     * appointment, maybe have a button for scanned paperwork to pop the same screen."*
     *
     * ⛔ THE SAME DIALOG THE RIGHT-CLICK OPENS, not a second one. Two viewers of the
     * same evidence would drift, and the one somebody happened to open would decide
     * what they believed was on file.
     */
    onPaperwork?: (appt: AppointmentDto) => void;
    /** After a notifiable action (approve/cancel/no-show), ask whether to email the carrier. */
    onNotify?: (appt: AppointmentDto, event: NotifyEvent) => void;
}

export function AppointmentDetailDialog({
    open,
    onClose,
    appt,
    doors,
    getToken,
    onChanged,
    onReschedule,
    onEdit,
    onPaperwork,
    onNotify,
}: AppointmentDetailDialogProps) {
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    /**
     * ⭐ WHAT THE DRIVER'S PAPERWORK SAID (#132). Fetched separately from the
     * calendar on purpose: `PaperworkConflictUtc` only exists once migration 151 has
     * run, and the calendar must never depend on an unapplied migration (#127).
     *
     * ⚠️ FETCH FAILURES ARE SWALLOWED. This is supplementary detail on a dialog whose
     * job is approving and cancelling trucks; a 500 reading it must not put an error
     * banner over the buttons a scheduler came here to press.
     */
    const [paperwork, setPaperwork] = useState<PaperworkStatusDto | null>(null);
    useEffect(() => {
        if (!open) { setPaperwork(null); return; }
        let alive = true;
        (async () => {
            try {
                const r = await getAppointmentPaperwork(await getToken(), appt.appointmentId);
                if (alive) setPaperwork(r);
            } catch {
                if (alive) setPaperwork(null);
            }
        })();
        return () => { alive = false; };
    }, [open, appt.appointmentId, getToken]);

    async function reviewPaperwork() {
        setBusy('paperwork');
        try {
            await markPaperworkReviewed(await getToken(), appt.appointmentId);
            const r = await getAppointmentPaperwork(await getToken(), appt.appointmentId);
            setPaperwork(r);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(null);
        }
    }
    // Which destructive/reason action is awaiting in-app confirmation.
    const [confirmKind, setConfirmKind] = useState<'noshow' | 'cancel' | 'reject' | null>(null);
    /**
     * ⛔ True once the server has refused to release this outbound load until its
     * warnings are acknowledged (#97). Not predicted here — the UI attempts the
     * departure and lets the server say what is wrong, so there is exactly one
     * place the rule lives.
     */
    const [releaseBlocked, setReleaseBlocked] = useState(false);

    const door = doors.find((d) => d.dockDoorId === appt.dockDoorId);

    // Action availability by current status
    // A block is a door out-of-service window, not a truck: the truck-lifecycle
    // actions (arrive/depart/no-show/approve/reject) and content Edit don't apply.
    // It can still be moved/resized (reschedule) and removed (cancel).
    const isBlock = appt.direction === 'BLOCK';
    const isTerminal =
        appt.statusCode === 'DEPARTED' ||
        appt.statusCode === 'CANCELLED' ||
        appt.statusCode === 'NOSHOW';
    const canMarkArrived = !isBlock && (appt.statusCode === 'APPROVED' || appt.statusCode === 'REQUESTED');
    // No-show = upcoming appointment whose truck never came. Same source states
    // as arrive (REQUESTED/APPROVED); server enforces the same guard.
    const canMarkNoShow = !isBlock && (appt.statusCode === 'APPROVED' || appt.statusCode === 'REQUESTED');
    const canMarkDeparted = !isBlock && appt.statusCode === 'ARRIVED';
    // Approve/Reject are the primary actions on a carrier request and only make
    // sense while it's still REQUESTED. The server enforces the same guard.
    const canApprove = !isBlock && appt.statusCode === 'REQUESTED';
    const canReject = !isBlock && appt.statusCode === 'REQUESTED';
    const canReschedule = !isBlock && !isTerminal && appt.statusCode !== 'ARRIVED';
    const canCancel = !isTerminal;
    // Edit is allowed at every status — content corrections are sometimes
    // needed even after a terminal status (typo fix, post-hoc PO assignment).
    // Direction-changes specifically are restricted server-side to REQUESTED/APPROVED.
    const canEdit = !isBlock;

    const doAction = async (
        label: string,
        action: (token: string) => Promise<void>,
        notifyEvent?: NotifyEvent,
    ) => {
        setBusy(label);
        setError(null);
        try {
            const token = await getToken();
            await action(token);
            // Notifiable actions (approve/cancel/no-show) hand off to onNotify,
            // which reloads and opens the opt-in carrier-email prompt. Everything
            // else (edit/arrive/depart/reject) goes through onChanged (reload +
            // confirmation toast). onNotify is optional → fall back to onChanged.
            if (notifyEvent && onNotify) {
                onNotify(appt, notifyEvent);
            } else {
                onChanged();
            }
            onClose();
        } catch (err) {
            // ⛔ Not an error: the outbound release gate wants a signature.
            if (err instanceof ApiCallError && err.details.code === 'OutboundReleaseBlocked') {
                setReleaseBlocked(true);
            } else if (err instanceof ApiCallError) {
                setError(err.message);
            } else {
                setError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setBusy(null);
        }
    };

    const handleArrive = () =>
        doAction('arrive', (token) => apiMarkArrived(token, appt.appointmentId), 'arrived');
    const handleDepart = (override?: ReleaseOverrideDto) =>
        doAction('depart', (token) => apiMarkDeparted(token, appt.appointmentId, override), 'departed');

    /**
     * The scheduler signed it off. Runs the SAME depart path carrying the
     * acknowledgement, so the post-departure behaviour — reload, carrier notify
     * prompt, close — is identical whether or not there were warnings.
     */
    const handleReleaseConfirm = (override: ReleaseOverrideDto) => {
        setReleaseBlocked(false);
        void handleDepart(override);
    };
    const handleApprove = () =>
        doAction('approve', (token) => apiApprove(token, appt.appointmentId), 'approved');

    // Destructive / reason-bearing actions open an in-app confirmation rather
    // than a browser prompt. runConfirmed dispatches the pending one.
    const runConfirmed = (reason?: string) => {
        const kind = confirmKind;
        setConfirmKind(null);
        if (kind === 'noshow') return doAction('noshow', (t) => apiMarkNoShow(t, appt.appointmentId), 'noshow');
        if (kind === 'cancel') return doAction('cancel', (t) => apiCancel(t, appt.appointmentId), 'cancelled');
        if (kind === 'reject') return doAction('reject', (t) => apiReject(t, appt.appointmentId, reason ?? ''));
    };

    return (
        <>
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <DialogTitle>{appt.appointmentNumber}</DialogTitle>
                        <span
                            className={cn(
                                'text-xs px-2 py-0.5 rounded-full border font-medium',
                                STATUS_BADGE_CLASSES[appt.statusCode],
                            )}
                        >
                            {STATUS_LABEL[appt.statusCode]}
                        </span>
                    </div>
                    <DialogDescription>
                        {appt.carrierName ?? appt.carrierCode} → {appt.clientName ?? appt.clientCode}
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                        <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                        <div className="text-destructive">{error}</div>
                    </div>
                )}

                {/* ---- Details grid ---- */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <Field label="Door">
                        {door ? `${door.code} · ${door.name}` : (appt.dockDoorCode ?? 'Unassigned')}
                    </Field>
                    <Field label="Document">
                        {appt.documentType}
                        {appt.documentNumber && ` · ${appt.documentNumber}`}
                    </Field>

                    <Field label="PO Number">{appt.poNumber ?? '—'}</Field>
                    <Field label="Reference">{appt.referenceNumber ?? '—'}</Field>

                    {/**
                      * ⭐ THE CONTAINER NUMBER (2026-08-27). Pete asked for it on every
                      * surface a scheduler reads while holding paperwork — the chip's
                      * hover card, the kiosk tiles, the driver texts, and here.
                      *
                      * ⚠️ SHOWN ONLY WHEN THERE IS ONE, unlike PO and Reference above.
                      * Those two print an em dash because a load is EXPECTED to have one
                      * or the other and their absence is worth seeing. A container is
                      * containerised freight only, so a permanent "Container —" on every
                      * dry van would be a row that never says anything.
                      *
                      * ⛔ MONOSPACED AND NOT TRUNCATED — it is read against a printed
                      * document, and a container number cut short still looks like a
                      * match, which is worse than not showing it at all.
                      */}
                    {/**
                      * ---- ⛔⚤ ~~shown only when there is one~~ — REVERSED 2026-09-25 ----
                      *
                      * Pete asked for the vehicle identifiers to be permanent rows, em dash
                      * and all, the same as PO and Reference above.
                      *
                      * ⚠️ THE 2026-08-27 REASONING WAS GOOD AND IS NOW OUTWEIGHED. It said a
                      * permanent "Container —" on every dry van is a row that never says
                      * anything, and that is true taken alone. What it did not weigh is that
                      * a DISAPPEARING row moves every row beneath it, so the same dialog has
                      * two different shapes and the eye has to re-find Scheduled each time.
                      *   ** A ROW THAT VANISHES WHEN EMPTY SAVES A LINE AND SPENDS A
                      *      POSITION. THE POSITION IS WORTH MORE ON A SCREEN SOMEBODY OPENS
                      *      FIFTY TIMES A DAY. **
                      *
                      * ⛔ AND AN EMPTY ROW IS NOT NOTHING — it says "no container was
                      * recorded", which on a load that should have had one is exactly the
                      * fact a scheduler is looking for. The hidden version could not say it.
                      *
                      * ⚠️ TRAILER AND PLATE ARE NOT HERE YET, AND THAT IS NOT AN OVERSIGHT.
                      * Pete asked for all three. `Appointment` carries `containerNumber` and
                      * does NOT carry `trailerNumber` or `trailerPlate` — those live on the
                      * CHECK-IN record, because a driver supplies them at the gate. Putting
                      * them here means the API joining the arrival onto the appointment,
                      * which is a backend change and ships with the schema batch.
                      *   ** A FIELD THE SCREEN CANNOT REACH IS A BACKEND TASK WEARING A UI
                      *      TASK'S CLOTHES. **
                      */}
                    <Field label="Container">
                        {appt.containerNumber
                            ? <span className="font-mono [overflow-wrap:anywhere]">
                                  {appt.containerNumber}
                              </span>
                            : '—'}
                    </Field>

                    <Field label="Scheduled">
                        <span className="text-tabular">
                            {format(parseISO(appt.scheduledStartUtc), 'MMM d, h:mm a')}
                            {' – '}
                            {format(parseISO(appt.scheduledEndUtc), 'h:mm a')}
                        </span>
                    </Field>
                    <Field label="Source">
                        {appt.requestedByCarrier ? 'Carrier request' : 'Scheduler-created'}
                    </Field>

                    {appt.actualArrivalUtc && (
                        <Field label="Arrived">
                            <span className="text-tabular">
                                {format(parseISO(appt.actualArrivalUtc), 'MMM d, h:mm a')}
                            </span>
                        </Field>
                    )}
                    {appt.actualDepartureUtc && (
                        <Field label="Departed">
                            <span className="text-tabular">
                                {format(parseISO(appt.actualDepartureUtc), 'MMM d, h:mm a')}
                            </span>
                        </Field>
                    )}

                    {appt.requiredCapabilities.length > 0 && (
                        <Field label="Required" className="col-span-2">
                            <div className="flex flex-wrap gap-1">
                                {appt.requiredCapabilities.map((c) => (
                                    <span
                                        key={c}
                                        className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground font-medium uppercase tracking-wide"
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </Field>
                    )}

                    {/* ⭐ THE DRIVER'S PAPERWORK vs THIS BOOKING (#132).
                        Rendered only when something actually happened — an
                        always-present empty panel is noise on the screen a scheduler
                        uses most. */}
                    {paperwork?.lastEvent && (
                        <div className="col-span-2">
                            <div className={cn(
                                'rounded-md border p-3 text-sm',
                                paperwork.conflictUtc
                                    ? 'border-amber-500/40 bg-amber-500/10'
                                    : 'border-border bg-muted/40',
                            )}>
                                <div className="font-medium">
                                    {paperwork.conflictUtc
                                        ? "The driver's paperwork disagrees with this appointment"
                                        : "Filled in from the driver's paperwork"}
                                </div>

                                {/* ⛔ BOTH VALUES, SIDE BY SIDE. Pete: "both sets of
                                    data should be saved in a log somewhere for the
                                    appointment." A count would send somebody to the
                                    audit trail to find out which field and which
                                    numbers. */}
                                {(paperwork.lastEvent.detail?.conflicts ?? []).length > 0 && (
                                    <ul className="mt-1.5 space-y-1">
                                        {paperwork.lastEvent.detail!.conflicts!.map((c, i) => (
                                            <li key={i} className="text-xs">
                                                <span className="font-medium">{c.field}</span>
                                                {' — appointment '}
                                                <code className="font-mono">{c.appointmentValue}</code>
                                                {', paperwork '}
                                                <code className="font-mono">{c.driverValue}</code>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {Object.keys(paperwork.lastEvent.detail?.filled ?? {}).length > 0 && (
                                    <div className="mt-1.5 text-xs text-muted-foreground">
                                        Filled in:{' '}
                                        {Object.entries(paperwork.lastEvent.detail!.filled!)
                                            .map(([k, v]) => `${k} ${v}`).join(', ')}
                                    </div>
                                )}

                                {paperwork.conflictUtc && (
                                    <div className="mt-2 flex items-center gap-2">
                                        {/* ⚠️ "Reviewed" clears the FLAG, never the record.
                                            If the driver was right, the scheduler edits the
                                            appointment — and that edit is audited on its own. */}
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => void reviewPaperwork()}
                                            disabled={busy !== null}
                                        >
                                            Mark reviewed
                                        </Button>
                                        <span className="text-xs text-muted-foreground">
                                            The appointment was not changed. To accept the
                                            driver's number, edit the appointment.
                                        </span>
                                    </div>
                                )}

                                {/* ⛔ Say so when the flag cannot exist yet, rather than
                                    showing a clean panel that looks like "no problems". */}
                                {!paperwork.migrationApplied && (
                                    <div className="mt-1.5 text-xs text-muted-foreground">
                                        Migration 151 is not applied, so disagreements are
                                        recorded in the appointment history but not flagged here.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ⭐ F183 (Decisions 341, 355) — a person's not-conforming flag on an
                        inbound load; dock-to-stock leaves a flagged load out. The panel
                        fetches its own state and hides itself on a read failure. */}
                    {appt.direction === 'INBOUND' && (
                        <ConformingPanel appointmentId={appt.appointmentId} getToken={getToken}
                                         disabled={busy !== null} />
                    )}

                    {/* ⭐ F187 (item 104, Decisions 251, 358) — the journey, inbound and
                        outbound, drawn from Pipeline's builder. Closed until asked for:
                        the read crosses to Pipeline. */}
                    <JourneyPanel appointmentId={appt.appointmentId} getToken={getToken} />

                    {appt.comments && (
                        <Field label="Comments" className="col-span-2">
                            <div className="text-sm whitespace-pre-wrap">{appt.comments}</div>
                        </Field>
                    )}
                </div>

                <DialogFooter>
                    <div className="flex flex-wrap gap-2 w-full justify-end">
                        {canApprove && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={handleApprove}
                                disabled={busy !== null}
                            >
                                {busy === 'approve' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                )}
                                Approve
                            </Button>
                        )}
                        {canReject && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmKind('reject')}
                                disabled={busy !== null}
                            >
                                {busy === 'reject' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Ban className="h-3.5 w-3.5" />
                                )}
                                Reject
                            </Button>
                        )}
                        {canMarkArrived && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={handleArrive}
                                disabled={busy !== null}
                            >
                                {busy === 'arrive' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <LogIn className="h-3.5 w-3.5" />
                                )}
                                Mark arrived
                            </Button>
                        )}
                        {canMarkDeparted && (
                            <Button
                                variant="default"
                                size="sm"
                                // ⚠️ WRAPPED, NOT PASSED DIRECTLY. onClick hands the
                                // handler a MouseEvent as its first argument, which
                                // would arrive as `override` and be serialised into
                                // the request body — a release "acknowledgement"
                                // made of a click event. It would not have thrown.
                                onClick={() => void handleDepart()}
                                disabled={busy !== null}
                            >
                                {busy === 'depart' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <LogOut className="h-3.5 w-3.5" />
                                )}
                                Mark departed
                            </Button>
                        )}
                        {/**
                          * ⭐ OFFERED AT EVERY STATUS, INCLUDING DEPARTED AND CANCELLED.
                          * A finished load's paperwork is exactly what somebody goes
                          * looking for when a carrier disputes a delivery weeks later —
                          * the case this is most useful in. Same rule the right-click
                          * item already follows.
                          */}
                        {onPaperwork && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    onClose();
                                    onPaperwork(appt);
                                }}
                                disabled={busy !== null}
                            >
                                <FileText className="h-3.5 w-3.5" />
                                Paperwork
                            </Button>
                        )}
                        {canEdit && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    onClose();
                                    onEdit(appt);
                                }}
                                disabled={busy !== null}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                            </Button>
                        )}
                        {canReschedule && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    onClose();
                                    onReschedule(appt);
                                }}
                                disabled={busy !== null}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                                Reschedule
                            </Button>
                        )}
                        {canMarkNoShow && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmKind('noshow')}
                                disabled={busy !== null}
                            >
                                {busy === 'noshow' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <UserX className="h-3.5 w-3.5" />
                                )}
                                Mark no-show
                            </Button>
                        )}
                        {canCancel && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmKind('cancel')}
                                disabled={busy !== null}
                            >
                                {busy === 'cancel' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <XCircle className="h-3.5 w-3.5" />
                                )}
                                {/**
                                  * ⭐⭐ "Cancel appointment", NOT "Cancel" (Pete, 2026-09-25).
                                  *
                                  * > *"Cancel needs to be Cancel appt or is it Void?"*
                                  *
                                  * ⛔ IT IS CANCEL. Void applies to an ARRIVAL - CheckInPanel
                                  * carries 'Void this arrival' and 'Cancel appointment' as two
                                  * separate menu items because they destroy two different records.
                                  *
                                  * ⚠️ AND THE BARE WORD WAS THE REAL PROBLEM. "Cancel" means
                                  * DISMISS THIS DIALOG in at least five other places in this app -
                                  * UploadAppointmentsDialog, DriversSection, PoFinder,
                                  * ConsigneeAdmin. Here alone it destroyed a record, and it sat
                                  * next to a Close button that actually did mean dismiss.
                                  *   ** ONE WORD WITH TWO OPPOSITE MEANINGS IS DANGEROUS IN
                                  *      WHICHEVER PLACE IS THE EXCEPTION. **
                                  *
                                  * ⭐ This dialog's own confirmation already said it in full -
                                  * "Cancel appointment?" and "Cancel this appointment? This can't
                                  * be undone." The button was the only thing that abbreviated.
                                  */}
                                Cancel appointment
                            </Button>
                        )}
                        {isTerminal && (
                            <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Completed — no further actions
                            </span>
                        )}
                        {/**
                          * ⭐ BORDERED, LIKE EVERY OTHER BUTTON ON THIS ROW (Pete, 2026-09-25):
                          * *"This screen needs a border around the close button so it matches
                          * ever other button on the screen."*
                          *
                          * ⚠️ `ghost` DREW IT AS BARE TEXT beside four bordered controls, so
                          * the one button that always works looked like the only one that was
                          * unavailable. Mark departed, Paperwork, Edit and Mark no-show are all
                          * `outline`; this is now the same.
                          *   ** A CONTROL DRAWN UNLIKE ITS NEIGHBOURS READS AS A DIFFERENT KIND
                          *      OF THING, AND "DISABLED" IS THE CHEAPEST GUESS. **
                          */}
                        <Button variant="outline" size="sm" onClick={onClose}>
                            Close
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <OutboundReleaseDialog
            open={releaseBlocked}
            appointmentId={appt.appointmentId}
            appointmentNumber={appt.appointmentNumber}
            getToken={getToken}
            busy={busy === 'depart'}
            onCancel={() => setReleaseBlocked(false)}
            onConfirm={handleReleaseConfirm}
        />

        {confirmKind && (
            <ConfirmDialog
                open={true}
                busy={busy !== null}
                destructive
                requireReason={confirmKind === 'reject'}
                title={
                    confirmKind === 'noshow' ? 'Mark no-show?'
                    : confirmKind === 'cancel' ? 'Cancel appointment?'
                    : 'Reject request?'
                }
                message={
                    confirmKind === 'noshow' ? 'Mark this appointment as a no-show? The truck did not arrive.'
                    : confirmKind === 'cancel' ? "Cancel this appointment? This can't be undone."
                    : 'The carrier will receive this reason in the decline email.'
                }
                confirmLabel={
                    confirmKind === 'noshow' ? 'Mark no-show'
                    : confirmKind === 'cancel' ? 'Cancel appointment'
                    : 'Reject request'
                }
                reasonLabel="Reason"
                reasonPlaceholder="e.g. No availability at that time"
                onConfirm={runConfirmed}
                onCancel={() => setConfirmKind(null)}
            />
        )}
        </>
    );
}

function Field({
    label,
    children,
    className,
}: {
    label: string;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={className}>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
            <div className="mt-0.5">{children}</div>
        </div>
    );
}
